import angulartics from 'angulartics';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { EventService } from '@go-modules/services/event/event.service';
import type { GoEvent } from '@go-modules/services/event/event.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

interface PendingCaptionState {
	filename: string;
	caption_media_id: number;
}

export class AccessibilityAttachmentsController implements ng.IOnInit, ng.IOnDestroy {

	public media;
	public groupId: number;
	public captionFileOptions: any;
	public audioDescriptionOptions: any;

	private captionMedia = null;
	private audioDescriptionMedia = null;
	private eventSubscription: Subscription;
	private origCaptionMediaId: number;

	/** @ngInject */
	constructor (
		private MediaModel,
		private $translate: ng.translate.ITranslateService,
		private $log: ng.ILogService,
		private UniversalMediaChooser,
		private goModal,
		private MediaVTT,
		private eventService: EventService,
		private SupplementaryMedia,
		private confirmModal,
		private $window: ng.IWindowService,
		private $analytics: angulartics.IAnalyticsService,
		public helpUrls
	) {}

	public $onInit (): void {
		this.updateAccessibilityMedia();
		this.restorePendingCaptionState();
		this.initEventWatcher();
		this.initThumbnailOptions();
	}

	public $onDestroy (): void {
		this.eventSubscription.unsubscribe();
	}

	public addCaptionMedia () {
		this.openUMC('fine-uploader/src/partials/caption-only-whitelist.json')
			.then(this.onCaptionUpload)
			.catch(this.$log.error);
	}

	public addAudioDescriptionMedia () {
		this.openUMC('fine-uploader/src/partials/audio-description-blacklist.json', this.MediaModel.TYPE.AUDIO)
			.then(this.onAudioDescriptionUpload)
			.catch(this.$log.error);
	}

	public removeCaptions () {
		return this.confirmModal.open({
			modalData: {
				title: this.$translate.instant('modal-confirm-delete-closed-caption_title'),
				message: this.$translate.instant('modal-confirm-delete-closed-caption_message'),
				yes: this.$translate.instant('common_delete'),
				no: this.$translate.instant('common_cancel')
			}
		}).result.then(() => {
			this.deleteCaptionMedia();
		}).catch(this.$log.error);
	}

	public removeAudioDescription () {
		return this.confirmModal.open({
			modalData: {
				title: this.$translate.instant('modal-confirm-delete-audio-description_title'),
				message: this.$translate.instant('modal-confirm-delete-audio-description_message'),
				yes: this.$translate.instant('common_delete'),
				no: this.$translate.instant('common_cancel')
			}
		}).result.then(() => {
			this.deleteAudioDescriptionMedia();
		}).catch(this.$log.error);
	}

	private updateAccessibilityMedia () {
		this.setCaptionMedia();
		this.setAudioDescriptionMedia();
	}

	private setAudioDescriptionMedia () {
		this.audioDescriptionMedia = this.media.getSupplementaryAudio().shift();
	}

	private getCaptionMedia () {
		const vtt = this.media.getMediaVTTCaptions().shift();
		const transformed = vtt != null ? this.MediaModel.transformMediaVttCaptionToMedia(vtt) : null;

		if (transformed !== null) {
			transformed.vtt_id = vtt.id;
			transformed.media_id = this.origCaptionMediaId || null;
		}

		return transformed;
	}

	private setCaptionMedia () {
		this.captionMedia = this.getCaptionMedia();
		if (this.captionMedia !== null && this.captionMedia.media_status === this.MediaModel.READY) {
			this.clearPendingCaptionState();
		}
	}

	private onCaptionUpload = (captionMedia) => {
		this.origCaptionMediaId = captionMedia.media_id;
		this.captionMedia = captionMedia;
		this.captionMedia.media_id = this.media.getId();
		const captionVtt = this.MediaVTT.transformMediaToMediaVttCaption(this.captionMedia);
		captionVtt.media_status = this.MediaModel.PENDING;
		this.media.addMediaVTT(captionVtt);
		this.persistPendingCaptionState({
			filename: this.captionMedia.filename,
			caption_media_id: this.origCaptionMediaId
		});
		this.setCaptionMedia();
	};

	private openUMC (blacklistFile, mediaType = null) {
		const umc = this.UniversalMediaChooser.get('umcModal');

		const defaultOptions = {
			headerOptions: [
				umc.MEDIA_TYPE.UPLOAD
			],
			defaultSection: umc.MEDIA_TYPE.UPLOAD,
			chooseAutomatically: true,
			uploadMinSizeLimit: 1,
			groupId: this.groupId,
			blacklist: null,
			extraParams: {
				video_media_id: this.media.getId()
			}
		};

		if (blacklistFile) {
			defaultOptions.blacklist = blacklistFile;
		}

		return this.goModal.open({
			modal: 'umc',
			modalData: {
				options: {
					mediaType,
					...defaultOptions
				}
			},
			animation: false
		}).result;
	}

	private initEventWatcher () {
		// Media sync event handler
		this.eventSubscription = this.eventService.events
			.pipe(filter((ev: GoEvent) => ev.name === EVENT_NAMES.MEDIA_SYNC))
			.subscribe((ev: GoEvent) => {

				// caption upload update (event fires for the caption media itself)
				const captionEventMediaId = this.origCaptionMediaId ?? this.captionMedia?.media_id;
				if (captionEventMediaId != null &&
					parseInt(captionEventMediaId, 10) === parseInt(ev.data.media_id, 10)) {
					const newStatus = ev.data.media_status === this.MediaModel.READY
						? this.MediaModel.READY
						: this.MediaModel.PENDING;
					this.captionMedia.media_status = newStatus;
					this.captionMedia.filename = ev.data.filename ?? this.captionMedia.filename;
					this.captionMedia.media_url = ev.data.media_url ?? this.captionMedia.media_url;

					this.media.media_vtt = this.media.media_vtt.map((item) => {
						const matchesExisting = item.id === this.captionMedia.vtt_id ||
							item.file === ev.data.filename_original ||
							item.file === this.captionMedia.filename;

						if (matchesExisting) {
							item.file = ev.data.filename ?? item.file;
							item.media_status = newStatus;
							item.media_url = ev.data.media_url ?? item.media_url;
						}
						return item;
					});

					this.setCaptionMedia();
					if (newStatus === this.MediaModel.READY) {
						this.clearPendingCaptionState();
						this.origCaptionMediaId = null;
					}
					return;
				}

				// video caption update (event fires for the parent video once captions attach)
				if (parseInt(this.media.getId(), 10) === parseInt(ev.data.media_id, 10) &&
					Array.isArray(ev.data.media_vtt)) {
					this.media.media_vtt = ev.data.media_vtt.map((item) => this.MediaVTT.newInstance(item));
					this.setCaptionMedia();
					this.clearPendingCaptionState();
				}

				if (this.media.supplementary_media && this.media.supplementary_media.length) {
					const supplementaryId = parseInt(this.media.supplementary_media[0].supplementary_media_id, 10);
					// audio description update
					if (supplementaryId === parseInt(ev.data.media_id, 10)) {
						const supplementaryMedia = this.SupplementaryMedia.newInstance(this.media.getId(), ev.data);
						this.media.supplementary_media[0] = supplementaryMedia;

						// Refresh
						this.setAudioDescriptionMedia();
					}
				}
			});
	}

	private initThumbnailOptions () {
		// Media thumbnail options
		this.captionFileOptions = {
			actions: false,
			preventPreview: true,
			preventEdit: true,
			hideTitle: true
		};

		this.audioDescriptionOptions = {
			actions: false,
			preventEdit: true,
			hideTitle: true
		};
	}

	private deleteCaptionMedia () {
		const caption = this.media.media_vtt.find((item) => item.id === this.captionMedia?.vtt_id)
			?? this.media.media_vtt?.[0];
		const cleanup = () => {
			if (caption) {
				this.media.removeMediaVTT(caption);
			}
			this.$analytics.eventTrack('delete cc file');
			this.clearPendingCaptionState();
			this.origCaptionMediaId = null;
			this.setCaptionMedia();
		};

		if (caption?.id) {
			this.media
				.deleteMediaVTT(caption)
				.then(cleanup)
				.catch(this.$log.error);
			return;
		}

		if (this.origCaptionMediaId !== null && this.origCaptionMediaId !== undefined) {
			this.MediaModel.delete({ media_id: this.origCaptionMediaId })
				.$promise
				.then(cleanup)
				.catch(this.$log.error);
			return;
		}

		cleanup();
	}

	private deleteAudioDescriptionMedia () {
		this.media
			.deleteSupplementaryMedia(this.audioDescriptionMedia)
			.then(() => {
				this.$analytics.eventTrack('delete ad file');
				this.setAudioDescriptionMedia();
			}).catch(this.$log.error);
	}

	private onAudioDescriptionUpload = (media) => {
		const supplementaryMedia = this.SupplementaryMedia.newInstance(this.media.getId(), media);
		supplementaryMedia.media_type = this.MediaModel.TYPE.AUDIO;
		this.media.addSupplementaryMedia(supplementaryMedia);
		supplementaryMedia.$save();
		this.setAudioDescriptionMedia();
	};

	private getPendingCaptionStorageKey (): string | null {
		return this.media?.getId ? `pending-caption-${this.media.getId()}` : null;
	}

	private persistPendingCaptionState (state: PendingCaptionState): void {
		const key = this.getPendingCaptionStorageKey();
		if (!key || !this.$window || !this.$window.sessionStorage) {
			return;
		}
		try {
			this.$window.sessionStorage.setItem(key, JSON.stringify(state));
		} catch (error) {}
	}

	private getPendingCaptionState (): PendingCaptionState | null {
		const key = this.getPendingCaptionStorageKey();
		if (!key || !this.$window || !this.$window.sessionStorage) {
			return null;
		}
		try {
			const stored = this.$window.sessionStorage.getItem(key);
			return stored ? JSON.parse(stored) : null;
		} catch (error) {
			return null;
		}
	}

	private clearPendingCaptionState (): void {
		const key = this.getPendingCaptionStorageKey();
		if (!key || !this.$window || !this.$window.sessionStorage) {
			return;
		}
		try {
			this.$window.sessionStorage.removeItem(key);
		} catch (error) {}
	}

	private restorePendingCaptionState (): void {
		const state = this.getPendingCaptionState();
		if (state === null
			|| this.media.getMediaVTTCaptions().length > 0
			|| this.captionMedia !== null) {
			if (this.media.getMediaVTTCaptions().length > 0) {
				this.clearPendingCaptionState();
			}
			return;
		}

		this.origCaptionMediaId = state.caption_media_id;
		const placeholder = this.MediaVTT.newInstance({
			file: state.filename,
			type: {
				name: this.MediaVTT.TYPE.CAPTION
			}
		});
		this.media.addMediaVTT(placeholder);
		this.setCaptionMedia();
	}
}
